import React from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import clientReviewViewModel from '../../../view-model/client/client-review-view-model';
import { Star } from 'lucide-react';

interface ReviewFormProps {
  onSubmit: (e: React.FormEvent) => Promise<void>;
}

/**
 * Composant formulaire d'avis client simplifié
 */
const ReviewForm: React.FC<ReviewFormProps> = observer(({ onSubmit }) => {
  const { t } = useTranslation();

  // Fonction pour générer les étoiles
  const renderStars = () => {
    return (
      <div className="flex items-center justify-center my-4">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={28}
            onClick={() => clientReviewViewModel.setRating(star)}
            className={`mx-1 cursor-pointer ${
              star <= clientReviewViewModel.rating
                ? 'text-yellow-400 fill-yellow-400'
                : 'text-gray-300 dark:text-gray-600'
            }`}
          />
        ))}
      </div>
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (clientReviewViewModel.isFormValid) {
      await clientReviewViewModel.submitReview();
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
      <h1 className="text-xl font-bold text-center text-primary mb-4">
        {t('reviews.title')}
      </h1>
      
      <form onSubmit={handleSubmit}>
        <div className="mb-6 text-center">
          <h2 className="text-base font-medium text-gray-700 mb-2">
            {t('reviews.rateExperience')}
          </h2>
          
          {renderStars()}
          
          {clientReviewViewModel.rating > 0 && (
            <p className="text-sm text-gray-600 mt-2">
              {clientReviewViewModel.rating}/5
            </p>
          )}
        </div>
        
        <div className="mb-6">
          <textarea
            className="w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            rows={4}
            placeholder={t('reviews.commentPlaceholder') || ''}
            value={clientReviewViewModel.comment}
            onChange={(e) => clientReviewViewModel.setComment(e.target.value)}
          />
        </div>
        
        <div className="flex justify-center">
          <button
            type="submit"
            disabled={!clientReviewViewModel.isFormValid || clientReviewViewModel.isSubmitting}
            className="flex items-center justify-center px-6 py-3 bg-primary hover:bg-primary-light text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[200px]"
          >
            {clientReviewViewModel.isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('reviews.submit')}
              </>
            ) : (
              <>
                <Star size={16} className="mr-2" />
                {t('reviews.send')}
              </>
            )}
          </button>
        </div>
        
        {clientReviewViewModel.error && (
          <p className="text-sm text-red-600 dark:text-red-400 mt-2 text-center">
            {clientReviewViewModel.error}
          </p>
        )}
      </form>
    </div>
  );
});

export default ReviewForm;
